import {
    buffer,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    share,
    startWith,
    Subject,
    takeUntil
} from "rxjs";
import {singleton} from "tsyringe";
import {ArrayHelper} from "../helpers/array.helper";
import {BaseMessage} from "./base-message.interface";
import {ListenerInfo} from "./listener-info.interface";

@singleton()
export class MessageBroker {

    private readonly _messageSubject = new Subject<BaseMessage<any>>();

    private readonly _messageSubject$: Observable<BaseMessage<any>>;

    private _publishEnabled = true;

    constructor() {
        this._messageSubject$ = this._messageSubject.pipe(
            distinctUntilChanged(
                (previous, current) => previous.type === current.type && previous.identityHash === current.identityHash
            ),
            share()
        );
    }

    public get messages$(): Observable<BaseMessage<any>> {
        return this._messageSubject$;
    }

    public publish<T extends Record<string, any> = Record<string, any>>(message: BaseMessage<T>): void {
        if (!this._publishEnabled) {
            return;
        }
        this._messageSubject.next(message);
    }

    public registerListener<T extends Record<string, any>>(scope: any, listenerInfo: ListenerInfo<T>): void {
        let stream = this.messages$.pipe(
            filter(message => {
                return message.type === listenerInfo.messageType && (!listenerInfo.filterFn || listenerInfo.filterFn(message))
            })
        );

        if (listenerInfo.disconnect) {
            stream = stream.pipe(
                takeUntil(listenerInfo.disconnect)
            );
        }

        if (listenerInfo.bufferTime) {
            const timer$ = stream.pipe(
                startWith(null),  // Startet sofort, um auf die erste Nachricht zu warten
                debounceTime(listenerInfo.bufferTime) // Wartet auf das Ende des Zeitintervalls nach der letzten Nachricht
            );

            stream.pipe(
                buffer(timer$), // Verwendet timer$ als Signal, um den Puffer zu senden
                map(messages => ArrayHelper.unique(messages, message => message.identityHash)), // filtert doppelte messages$ heraus
                filter(messages => messages.length > 0) // Filtert leere Puffer heraus
            ).subscribe(
                listenerInfo.method.bind(scope)
            )
        } else {
            stream.subscribe(
                (message: BaseMessage<T>) => listenerInfo.method.call(scope, [message])
            );
        }
    }
}
